import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";

/**
 * ForcePasswordChangeModal — shown after login when user.must_change_password=true.
 * Cannot be dismissed; user must set a new password to continue.
 *
 * Props:
 *   open, onSuccess()
 */
const ForcePasswordChangeModal = ({ open, onSuccess }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("A nova senha precisa de no mínimo 8 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não conferem");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ new_password: newPassword }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Erro ao alterar senha");
      toast.success("Senha alterada com sucesso!");
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        data-testid="force-password-modal"
      >
        <DialogHeader>
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-3">
            <ShieldAlert className="w-7 h-7 text-amber-700" />
          </div>
          <DialogTitle className="text-center text-xl">Defina sua nova senha</DialogTitle>
          <DialogDescription className="text-center">
            Por segurança, no primeiro login você precisa criar uma senha pessoal.
            A senha padrão não poderá mais ser usada após esta troca.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="new_password">Nova senha</Label>
            <div className="relative mt-1">
              <Input
                id="new_password"
                type={show ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                required
                minLength={8}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirm_password">Confirme a nova senha</Label>
            <Input
              id="confirm_password"
              type={show ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="mt-1"
              data-testid="confirm-password-input"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <p className="font-semibold mb-1">Dicas de segurança:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Misture letras, números e símbolos</li>
              <li>Não use dados pessoais (nome, CPF, datas)</li>
              <li>Guarde a senha em um lugar seguro</li>
            </ul>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary/90"
            data-testid="submit-new-password-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              "Salvar nova senha"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordChangeModal;
